/* eslint-disable no-undef, no-case-declarations, no-param-reassign */
import React, { useRef, useEffect, memo } from 'react'
import last from 'ramda/src/last'
import split from 'ramda/src/split'
import { v4 as uuid } from 'uuid'
import { playerStyles } from 'root/src/client/web/commonStyles'
import withStyles from '@material-ui/core/styles/withStyles'
import classNames from 'classnames'
import mixpanel from 'root/src/client/logic/analytics/mixpanel'
import { mixpanelEvents } from 'root/src/shared/constants/mixpanel'
import { getEmbedUrl } from 'root/src/client/v2/common/helpers/getEmbedUrl'
import outputs from 'root/cfOutput'

/**
 * This component assumes Twitch and yt player scripts are loaded.
	*
	* Example implementation of yt and twitch script load in
	* src/client/web/list/listComponents/VideoCardListModule.js
 */

const TwitchPlayer = memo(({
	videoId, classes, autoplay, onChangeItemCallbackArr, timestampQuery, mediaType,
	completedDare, muted, className, videoURL, onPlay,
}) => {
	const playerRef = useRef()
	const iframeRef = useRef()

	const timestamp = last(split('=', timestampQuery))
	const onVideoStart = () => {
		if (onPlay) {
			onPlay()
		}
		mixpanel.time_event(mixpanelEvents.videoStart)
	}
	const onVideoPause = () => {
		const player = playerRef.current
		mixpanel.track(mixpanelEvents.videoStop,
			{ videoLength: player.getDuration(), videoPlayer: 'TwitchPlayer' })
	}
	useEffect(() => {
		const id = uuid()
		iframeRef.current.id = id
		switch (mediaType) {
		case 'video':
		case 'collection':
			playerRef.current = new Twitch.Player(id, {
				width: '100%',
				height: '100%',
				[mediaType]: videoId,
				autoplay: !!autoplay,
				time: timestamp,
				preload: 'none',
				parent: [outputs.domainName],
			})
			playerRef.current.addEventListener(Twitch.Player.READY, () => {
				const player = playerRef.current
				player.setMuted(true)
				iframeRef.current.style.backgroundColor = 'transparent'
				if (onChangeItemCallbackArr) {
					onChangeItemCallbackArr.current = [...onChangeItemCallbackArr.current, () => {
						if (player.isPaused() === false) {
							player.pause()
						}
					}]
				}
			})
			playerRef.current.addEventListener(Twitch.Player.PLAY, onVideoStart)
			playerRef.current.addEventListener(Twitch.Player.PAUSE, onVideoPause)
			break
		case 'clip':
			const player = document.createElement('iframe')
			// eslint-disable-next-line no-shadow
			player.src = getEmbedUrl(videoURL, { autoplay: Boolean(autoplay), muted })
			player.height = '100%'
			player.width = '100%'
			player.frameBorder = '0'
			player.title = videoId
			player.allowFullscreen = true
			player.allow = 'autoplay'
			player.preload = 'none'
			playerRef.current = player
			iframeRef.current.appendChild(player)
			if (onChangeItemCallbackArr) {
				// eslint-disable-next-line no-param-reassign
				onChangeItemCallbackArr.current = [...onChangeItemCallbackArr.current, () => {
					player.src = getEmbedUrl(videoURL, { autoplay: false, muted })
				}]
			}
			break
		default:
		}
		return () => {
			if (iframeRef.current) {
				iframeRef.current.innerHTML = ''
			}
		}
	}, [videoId])
	useEffect(() => {
		if (mediaType !== 'clip') {
			window.addEventListener('beforeunload', onVideoPause)
			return () => {
				window.removeEventListener('beforeunload', onVideoPause)
				onVideoPause()
			}
		}

		return () => {}
	}, [])

	return (
		<div className={classNames(classes.iframeContainer, {
			[classes.completedDareIframeContainer]: completedDare,
		})}
		>
			<div
				ref={iframeRef}
				className={classNames(classes.iframe, className)}
			/>
		</div>
	)
})

export default withStyles(playerStyles, { name: 'MuiTwitchPlayer' })(TwitchPlayer)
